/**
 * Lazily-created FaceLandmarker, shared across the app.
 *
 * The WASM runtime and the 3.8 MB model are expensive to load, so we build one
 * instance and keep it. Assets are served from our own origin — see
 * scripts/setup-mediapipe.mjs, which stages them into public/mediapipe/.
 */
let landmarkerPromise = null;

async function createLandmarker() {
  // Imported lazily rather than at module scope so a few hundred KB stay out
  // of the initial download; nothing needs it until try-on opens.
  const { FaceLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");

  const fileset = await FilesetResolver.forVisionTasks("/mediapipe/wasm");

  const baseOptions = { modelAssetPath: "/mediapipe/face_landmarker.task" };

  const options = {
    runningMode: "VIDEO",
    numFaces: 1,
    outputFacialTransformationMatrixes: false,
    outputFaceBlendshapes: false,
  };

  try {
    return await FaceLandmarker.createFromOptions(fileset, {
      baseOptions: { ...baseOptions, delegate: "GPU" },
      ...options,
    });
  } catch {
    // Some machines (older integrated GPUs, locked-down browsers) can't create
    // the WebGL context MediaPipe wants. CPU is slower but always available.
    return FaceLandmarker.createFromOptions(fileset, {
      baseOptions: { ...baseOptions, delegate: "CPU" },
      ...options,
    });
  }
}

export function getFaceLandmarker() {
  if (!landmarkerPromise) {
    landmarkerPromise = createLandmarker();
  }
  return landmarkerPromise;
}
